import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';

export function LoginPage() {
  const qc = useQueryClient();
  const [tenant, setTenant] = useState('mediresponse');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/login', {
        tenant,
        email,
        password,
        ...(totp ? { totp } : {}),
      });
      await qc.invalidateQueries({ queryKey: ['me'] });
    } catch (err) {
      const body = (err as { body?: { mfaRequired?: boolean } }).body;
      if (body?.mfaRequired) {
        // The password was right; only the second factor is outstanding. Say so,
        // rather than showing "invalid credentials" and making the user doubt
        // what they just typed.
        setMfaRequired(true);
        setError('Enter the 6-digit code from your authenticator app.');
      } else {
        setError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <form onSubmit={(e) => void submit(e)} className="w-full max-w-sm space-y-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">Medi Response One</h1>
          <p className="mt-1 text-sm text-slate-400">Sign in to continue</p>
        </div>

        <label className="block">
          <span className="text-xs text-slate-400">Organisation</span>
          <input
            value={tenant}
            onChange={(e) => setTenant(e.target.value)}
            className="mt-1 w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-slate-200"
          />
        </label>

        <label className="block">
          <span className="text-xs text-slate-400">Email</span>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-slate-200"
          />
        </label>

        <label className="block">
          <span className="text-xs text-slate-400">Password</span>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-slate-200"
          />
        </label>

        {mfaRequired && (
          <label className="block">
            <span className="text-xs text-slate-400">Authenticator code</span>
            <input
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              value={totp}
              onChange={(e) => setTotp(e.target.value)}
              className="mt-1 w-full rounded-lg border border-line bg-surface-2 px-3 py-2 font-mono text-sm tracking-widest text-slate-200"
            />
          </label>
        )}

        {error && <p className="text-sm text-amber-400">{error}</p>}

        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-lg bg-sky-600 py-2 text-sm font-medium text-white disabled:opacity-40"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
